import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Brain, Activity, Shield, Layers } from 'lucide-react';
import { getCortexAnalytics } from '../../services/cortexAnalytics';
import { useSubscription } from '../../hooks/useSubscription';
import LockedWidgetOverlay from '../../components/common/LockedWidgetOverlay';
import EmotionalHeatmap from './widgets/EmotionalHeatmap';
import DisciplineTracker from './widgets/DisciplineTracker';
import EmotionOutcomeChart from './widgets/EmotionOutcomeChart';
import RuleBreakingPanel from './widgets/RuleBreakingPanel';
import PatternBadges from './widgets/PatternBadges';
import TagEmotionPieChart from './widgets/TagEmotionPieChart';
import ScoreBalance from './widgets/ScoreBalance';
import ScoreCorrelation from './widgets/ScoreCorrelation';
import ScoreTrends from './widgets/ScoreTrends';
import { trackCortexTabViewed, trackCortexRefreshed } from '../../services/analytics';

const TABS = [
  { id: 'overview',      label: 'Overview',      icon: Brain,    description: 'Emotional scores and trade outcomes' },
  { id: 'discipline',    label: 'Discipline',     icon: Shield,   description: 'Rule adherence and behavioral patterns' },
  { id: 'deep-analysis', label: 'Deep Analysis',  icon: Layers,   description: 'Tag correlations and long-term trends' },
];

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className="h-3.5 w-0.5 rounded-full bg-primary inline-block" />
    <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{children}</span>
  </div>
);

const CortexAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [computedAt, setComputedAt] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const { isBasicOrAbove, isProOrAbove } = useSubscription();

  const heatmapData = useMemo(() => {
    if (!data?.heatmap_data) return [];
    if (isProOrAbove) return data.heatmap_data;
    if (isBasicOrAbove) return data.heatmap_data.slice(-30);
    return [];
  }, [data?.heatmap_data, isBasicOrAbove, isProOrAbove]);

  const fetchData = useCallback(async (refresh = false) => {
    try {
      if (refresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const result = await getCortexAnalytics(refresh);

      if (result.success) {
        setData(result);
        setComputedAt(result.computed_at);
      } else {
        setError(result.error || 'Failed to load analytics');
      }
    } catch (err) {
      setError(err.message || 'Failed to load Cortex Analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleRefresh = () => {
    trackCortexRefreshed();
    fetchData(true);
  };

  if (loading) {
    return (
      <div className="pt-16 min-h-screen bg-background">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-sm text-muted-foreground">Loading Cortex...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-16 min-h-screen bg-background">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3 text-center">
              <p className="text-sm text-red-400">{error}</p>
              <button
                onClick={() => fetchData(false)}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeTabDef = TABS.find(t => t.id === activeTab);

  return (
    <div className="pt-16 min-h-screen bg-background">
      {/* Sticky header */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-16 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Brain className="w-5 h-5 text-primary flex-shrink-0" />
            <div>
              <h1 className="text-xl font-semibold tracking-tight text-foreground">Cortex</h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                Know why you trade the way you do
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {computedAt && (
              <span className="text-xs text-muted-foreground hidden md:block">
                Updated {new Date(computedAt).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric',
                  hour: 'numeric', minute: '2-digit',
                })}
              </span>
            )}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-all duration-150 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-end px-4 sm:px-6 lg:px-8 gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); trackCortexTabViewed({ tab: tab.id }); }}
                className={`relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all duration-150 ${
                  isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
                {isActive && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Tab context line */}
        {activeTabDef?.description && (
          <p className="text-sm text-muted-foreground -mt-2">{activeTabDef.description}</p>
        )}

        {/* ── Overview Tab ── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <section>
              <SectionLabel>Psychology Scores</SectionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ScoreBalance data={data?.score_analytics} />
                <EmotionOutcomeChart data={data?.emotion_outcome || []} />
              </div>
            </section>

            <section>
              <SectionLabel>Emotion Calendar</SectionLabel>
              {isBasicOrAbove ? (
                <div>
                  <EmotionalHeatmap data={heatmapData} />
                  {!isProOrAbove && heatmapData.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                      Showing last 30 days only.
                      <a href="/pricing" className="text-primary hover:underline">Upgrade to Pro for full history →</a>
                    </p>
                  )}
                </div>
              ) : (
                <LockedWidgetOverlay
                  title="Emotion Heatmap"
                  benefit="See your emotional state patterns visualized across the calendar year"
                  fullWidth
                  requiredPlan="basic"
                />
              )}
            </section>
          </div>
        )}

        {/* ── Discipline Tab ── */}
        {activeTab === 'discipline' && (
          <div className="space-y-8">
            <section>
              <SectionLabel>Rule Adherence</SectionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isBasicOrAbove ? (
                  <DisciplineTracker data={data?.discipline_tracker || []} />
                ) : (
                  <LockedWidgetOverlay
                    title="Discipline Score Tracker"
                    benefit="Track your weekly discipline scores and identify patterns over time"
                    requiredPlan="basic"
                  />
                )}

                <RuleBreakingPanel
                  feedData={data?.rule_breaking_feed || []}
                  calendarData={data?.rule_breaking_calendar || []}
                  isBasicOrAbove={isBasicOrAbove}
                  isProOrAbove={isProOrAbove}
                />
              </div>
            </section>

            <section>
              <SectionLabel>Behavioral Patterns</SectionLabel>
              {isBasicOrAbove ? (
                <PatternBadges data={data?.badges || []} />
              ) : (
                <LockedWidgetOverlay
                  title="Psychological Pattern Badges"
                  benefit="Discover your behavioral patterns and track progress toward becoming a better trader"
                  fullWidth
                  requiredPlan="basic"
                />
              )}
            </section>
          </div>
        )}

        {/* ── Deep Analysis Tab ── */}
        {activeTab === 'deep-analysis' && (
          <div className="space-y-8">
            <section>
              <SectionLabel>Tags &amp; Emotions by Symbol</SectionLabel>
              {isBasicOrAbove ? (
                <TagEmotionPieChart data={data?.tag_emotion_by_symbol || {}} />
              ) : (
                <LockedWidgetOverlay
                  title="Tags & Emotions Distribution"
                  benefit="Understand which emotional states and rule violations are tied to specific trading pairs"
                  fullWidth
                  requiredPlan="basic"
                />
              )}
            </section>

            <section>
              <SectionLabel>Score Correlations &amp; Trends</SectionLabel>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {isBasicOrAbove ? (
                  <ScoreCorrelation data={data?.score_analytics} />
                ) : (
                  <LockedWidgetOverlay
                    title="Score vs P&L Correlation"
                    benefit="See how your emotional, risk, and strategy scores correlate with actual trading profits"
                    requiredPlan="basic"
                  />
                )}

                {isBasicOrAbove ? (
                  <ScoreTrends data={data?.score_analytics} />
                ) : (
                  <LockedWidgetOverlay
                    title="Score Trends Over Time"
                    benefit="Track how your emotional discipline, risk management, and strategy adherence evolve"
                    requiredPlan="basic"
                  />
                )}
              </div>
            </section>
          </div>
        )}

        {/* Disclaimer */}
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground italic">
            This analysis reflects patterns in your trading journal and is for educational and self-reflection purposes only.
            It does not constitute investment advice, trading signals, or recommendations.
            All trading decisions are solely your responsibility.
          </p>
        </div>

      </div>
    </div>
  );
};

export default CortexAnalytics;
